// Домашний экран: отчёты карточками с миниатюрой титула (или страницы 1 из PDF, если отчёт уже собирали),
// поиск, сортировка, вид «плитки/список», новый отчёт (окно 'project.create' - функция sto_intel может заменить
// его своим мастером), «Открыть .docx» и импорт (.md/.zip), примеры, копия, архив, корзина с восстановлением.
// Файл можно просто перетащить на экран. Клавиши на карточке: Enter - открыть, F2 - переименовать, Delete - в корзину.
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../core/api';
import { ctx, runCommand } from '../core/ctx';
import { registry, type DialogProps } from '../core/registry';
import { readPref, useApp, writePref } from '../core/store';
import type { ProjectInfo } from '../core/types';
import { Btn, Empty, Field, Modal, MenuList, Popover, Segmented } from './controls';
import { Icon } from './icons';
import { useRegistry } from './hooks';
import { askConfirm, askText } from './prompts';
import { shellApi, type ProjectCard, type TrashItem } from '../features/shell/shellApi';
import { download } from '../features/shell/file';
import { AccountMenu, AdminButton, QuotaNote, WelcomeCard } from '../features/auth/account';
import { needSubscription, SubscriptionBanner } from '../features/auth/billing';
import { checkSession, useAuth } from '../features/auth/session';

export const TYPE_NAMES: Record<string, string> = {
  lab: 'Лабораторная работа', pr: 'Практическая работа', kr: 'Контрольная работа', rgr: 'Расчётно-графическая работа',
  referat: 'Реферат', essay: 'Эссе', kursovaya: 'Курсовая работа', kp: 'Курсовой проект', practice: 'Отчёт о практике',
  sheet: 'Чистый лист',
};
const TYPE_ICON: Record<string, string> = { lab: 'sigma', pr: 'task', kr: 'check', rgr: 'formula', referat: 'book', essay: 'text',
  kursovaya: 'student', kp: 'student', practice: 'library', sheet: 'file' };

type P = ProjectInfo & { card?: ProjectCard | null };

export function fmtAgo(s?: string) {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(+d)) return s;
  const diff = (Date.now() - +d) / 1000;
  if (diff < 60) return 'только что';
  if (diff < 3600) return `${Math.floor(diff / 60)} мин назад`;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const t = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (+d >= +today) return `сегодня в ${t}`;
  if (+d >= +today - 86400000) return `вчера в ${t}`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}

const SORTS = { modified: 'по изменению', name: 'по имени', type: 'по виду работы', discipline: 'по дисциплине', created: 'по созданию' } as const;
type SortKey = keyof typeof SORTS;

/** Миниатюра титульного листа по данным работы (форма Прил. Б в упрощённом виде). */
function TitleThumb({ p }: { p: P }) {
  const c = p.card;
  if (c?.pdf) {
    return <img className="thumb-img" alt="" loading="lazy" src={api.previewPageUrl(p.id, 1, 36, String(c.pdfTime))} />;
  }
  return (
    <div className="thumb-title" aria-hidden>
      <div className="tt-small">Министерство науки и высшего образования РФ</div>
      <div className="tt-small">{c?.university ? c.university.slice(0, 60) : 'САФУ имени М. В. Ломоносова'}</div>
      {c?.branch ? <div className="tt-small">{c.branch.slice(0, 50)}</div> : null}
      <div className="tt-gap" />
      <div className="tt-kind">{(TYPE_NAMES[p.type] || 'Отчёт').toUpperCase()}{p.number ? ` № ${p.number}` : ''}</div>
      <div className="tt-line">{p.discipline ? `по дисциплине «${p.discipline}»` : ''}</div>
      <div className="tt-topic">{p.title ? `«${p.title}»` : ''}</div>
      <div className="tt-gap" />
      <div className="tt-right">
        <div>Выполнил: {c?.student_short || c?.student || '…'}</div>
        {c?.group && <div>Группа {c.group}</div>}
        {c?.teacher && <div>Руководитель: {c.teacher}</div>}
      </div>
      <div className="tt-bottom">{c?.city || ''} {c?.year || new Date().getFullYear()}</div>
    </div>
  );
}

export function StartScreen() {
  const [list, setList] = useState<P[] | null>(null);
  const [dataDir, setDataDir] = useState('');
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState<SortKey>(() => (readPref('home.sort', 'modified') as SortKey) || 'modified');
  const [view, setView] = useState<'grid' | 'list'>(() => (readPref('home.view', 'grid') === 'list' ? 'list' : 'grid'));
  const [trash, setTrash] = useState<TrashItem[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [drag, setDrag] = useState(false);
  const [exMenu, setExMenu] = useState(false);
  const exRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const docxRef = useRef<HTMLInputElement>(null);
  const defaults = useApp((s) => s.defaults);
  const sections = useRegistry(registry.startSections);
  const canReveal = useAuth((s) => !!s.me?.capabilities.canReveal);

  const reload = () => Promise.all([
    api.listProjects().then((r) => { setList(r.projects as P[]); setDataDir(r.dataDir); }),
    shellApi.trash().then((r) => setTrash(r.items)).catch(() => setTrash([])),
  ]).then(() => checkSession()).catch((e) => ctx.toast(e.message, 'error', 8000));
  useEffect(() => { void reload(); document.title = 'Редактор СТО'; }, []);
  useEffect(() => { writePref('home.sort', sort); }, [sort]);
  useEffect(() => { writePref('home.view', view); }, [view]);

  const open = (id: string) => { location.hash = '#/p/' + encodeURIComponent(id); };
  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    try { await fn(); if (ok) ctx.toast(ok, 'ok'); await reload(); } catch (e: any) { ctx.toast(e.message, 'error', 8000); }
  };
  const importFile = (f: File) => (needSubscription() ? Promise.resolve() : act(async () => {
    ctx.toast(`Импорт «${f.name}»…`, 'info', 2500);
    const r = await api.importProject(f);
    open(r.id);
  }, 'Импортировано'));
  const create = async () => {
    if (needSubscription()) return;
    const id = await ctx.openDialog<string>('project.create');
    if (id) open(id);
  };
  const rename = async (p: P) => {
    const n = await askText({ title: 'Переименовать отчёт', label: 'Новое имя', value: p.name, ok: 'Переименовать', testid: 'rename-dialog' });
    if (n && n.trim() !== p.name) void act(() => api.renameProject(p.id, n.trim()), 'Переименовано');
  };
  const toTrash = async (p: P) => {
    const ok = await askConfirm({ title: 'Переместить в корзину?', text: `«${p.name}» можно будет вернуть из корзины внизу этой страницы.`, ok: 'В корзину', danger: true, testid: 'trash-confirm' });
    if (ok) void act(() => api.trashProject(p.id), 'Перемещено в корзину');
  };
  const zip = (p: P) => act(async () => {
    const r = await api.exportFile(p.id, 'zip');
    if (r.file) download(api.fileUrl(p.id, r.file));
  }, 'Архив скачан');

  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase().replace(/ё/g, 'е');
    const arr = (list || []).filter((p) => !q || `${p.name} ${p.title} ${p.discipline || ''} ${TYPE_NAMES[p.type] || ''} ${p.card?.teacher || ''}`
      .toLowerCase().replace(/ё/g, 'е').includes(q));
    const by: Record<SortKey, (a: P, b: P) => number> = {
      modified: (a, b) => b.mtime - a.mtime,
      created: (a, b) => (b.created || '').localeCompare(a.created || ''),
      name: (a, b) => a.name.localeCompare(b.name, 'ru'),
      type: (a, b) => (TYPE_NAMES[a.type] || '').localeCompare(TYPE_NAMES[b.type] || '', 'ru') || b.mtime - a.mtime,
      discipline: (a, b) => (a.discipline || 'я').localeCompare(b.discipline || 'я', 'ru') || b.mtime - a.mtime,
    };
    return [...arr].sort(by[sort]);
  }, [list, filter, sort]);

  const examples: any[] = defaults?.examples || [];
  return (
    <div className={'start' + (drag ? ' is-drag' : '')} data-testid="start"
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setDrag(false); }}
      onDrop={(e) => {
        e.preventDefault(); setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f && /\.(docx|docm|doc|rtf|odt|md|zip)$/i.test(f.name)) void importFile(f);
        else if (f) ctx.toast('Перетащите файл .docx (.doc, .rtf, .odt), .md или .zip', 'warn');
      }}>
      <header className="start-head">
        <span className="logo logo-big">СТО</span>
        <div className="start-head-text">
          <h1>Редактор отчётов по СТО САФУ 2026</h1>
          <div className="start-sub">Пишите содержание - оформление, нумерация, титул и проверка делаются сами.</div>
        </div>
        <span className="topbar-spacer" />
        <Btn icon="theme" title="Тёмная / светлая тема" onClick={() => runCommand('view.theme')} testid="home-theme" />
        <Btn icon="sto-book" label="Памятка по СТО" onClick={() => void ctx.openDialog('shell.guide')} testid="home-guide" />
        <AdminButton />
        <AccountMenu />
      </header>
      <SubscriptionBanner />
      <div className="start-actions">
        <Btn icon="new" label="Новый отчёт" primary onClick={create} testid="new-project" />
        <Btn icon="file" label="Чистый лист" desc="Только страницы текста по СТО - без титула, оглавления и служебных листов: вставил текст, выгрузил Word"
          onClick={() => { if (!needSubscription()) void act(async () => { open((await api.createProject({ name: 'Чистый лист', type: 'sheet' })).id); }); }} testid="new-sheet" />
        <Btn icon="file-up" label="Открыть .docx" desc="Импорт отчёта из Word с распознаванием структуры СТО" onClick={() => { if (!needSubscription()) docxRef.current?.click(); }} testid="open-docx" />
        <Btn icon="import" label="Импорт .md / .zip" desc="Markdown для writer или архив проекта" onClick={() => { if (!needSubscription()) fileRef.current?.click(); }} testid="import-project" />
        <Btn icon="sparkles" label="Через нейросеть" desc="Claude или ChatGPT пишут отчёт, сайт оформляет по СТО" onClick={() => void ctx.openDialog('auth.mcp', {})} testid="home-mcp" />
        {examples.length > 0 && (
          <div ref={exRef}>
            <Btn icon="book" label="Примеры" onClick={() => setExMenu(!exMenu)} testid="examples"><Icon name="down" size={12} /></Btn>
            {exMenu && (
              <Popover anchor={exRef.current} onClose={() => setExMenu(false)}>
                <MenuList onDone={() => setExMenu(false)} items={[{ id: 'ex-sheet', label: 'Чистый лист (только текст по СТО)', icon: 'file',
                  onClick: () => void act(async () => { open((await api.createProject({ name: 'Чистый лист', type: 'sheet' })).id); }) },
                ...examples.map((ex) => ({ id: 'ex-' + ex.id, label: ex.name, icon: 'book',
                  onClick: () => void act(async () => { const r = await api.createProject({ name: ex.name, type: ex.type || 'lab', template: 'example:' + ex.id }); open(r.id); }) }))]} />
              </Popover>
            )}
          </div>
        )}
        <input ref={docxRef} type="file" accept=".docx,.doc,.rtf,.odt" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void importFile(f); }} />
        <input ref={fileRef} type="file" accept=".md,.docx,.doc,.rtf,.odt,.zip" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void importFile(f); }} />
        <span className="topbar-spacer" />
        <div className="search-box">
          <Icon name="search" size={14} />
          <input className="search" placeholder="Поиск по имени, теме, дисциплине…" value={filter} onChange={(e) => setFilter(e.target.value)}
            data-testid="home-search" aria-label="Поиск отчёта" />
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Сортировка" data-testid="home-sort">
          {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <Segmented value={view} onChange={setView} testid="home-view"
          options={[{ id: 'grid', label: '', icon: 'grid' }, { id: 'list', label: '', icon: 'list' }]} />
      </div>

      <WelcomeCard />
      {list === null && <div className="muted pad">Загрузка…</div>}
      {list && list.length > 0 && (
        <div className="start-section-head">
          <span>{filter ? `Найдено: ${shown.length} из ${list.length}` : `Мои отчёты · ${list.length}`}</span>
        </div>
      )}
      {list && !list.length && (
        <Empty icon="sparkles" title="Здесь будут ваши отчёты"
          text={<>Создайте первый - выберите вид работы, и структура по СТО появится сама. Или перетащите сюда готовый .docx.</>}>
          <Btn icon="new" label="Создать первый отчёт" primary onClick={create} />
          <Btn icon="file-up" label="Открыть .docx" onClick={() => docxRef.current?.click()} />
        </Empty>
      )}
      {list && list.length > 0 && !shown.length && <div className="muted pad">Ничего не найдено по запросу «{filter}».</div>}
      <div className={view === 'grid' ? 'project-grid' : 'project-rows'} data-testid="project-list" role="list">
        {shown.map((p) => (
          <div key={p.id} className="project-card" data-testid="project-card" role="listitem" tabIndex={0} aria-label={p.name}
            onKeyDown={(e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === 'Enter') open(p.id);
              else if (e.key === 'F2') { e.preventDefault(); void rename(p); }
              else if (e.key === 'Delete') void toTrash(p);
            }}>
            <div className="project-thumb" onClick={() => open(p.id)}>
              <TitleThumb p={p} />
              <span className="project-type-ico"><Icon name={TYPE_ICON[p.type] || 'file'} size={14} /></span>
            </div>
            <div className="project-card-main" onClick={() => open(p.id)}>
              <div className="project-type">{TYPE_NAMES[p.type] || p.type}{p.number ? ` № ${p.number}` : ''}</div>
              <div className="project-name" title={p.name}>{p.name}</div>
              <div className="project-title" title={p.title}>{p.title || <span className="muted">без темы</span>}</div>
              <div className="project-meta">{p.discipline}</div>
              {p.card && (
                <div className="project-stats">
                  <span>{p.card.counts.words ? `${p.card.counts.words.toLocaleString('ru-RU')} сл.` : 'пока пусто'}</span>
                  {p.card.counts.figures > 0 && <span><Icon name="image" size={12} /> {p.card.counts.figures}</span>}
                  {p.card.counts.tables > 0 && <span><Icon name="table" size={12} /> {p.card.counts.tables}</span>}
                  {p.card.counts.formulas > 0 && <span><Icon name="formula" size={12} /> {p.card.counts.formulas}</span>}
                </div>
              )}
              <div className="project-date" title={p.modified}>Изменён {fmtAgo(p.modified)}</div>
              {p.error && <div className="project-error">{p.error}</div>}
            </div>
            <div className="project-card-actions">
              <Btn icon="rename" title="Переименовать" shortcut="F2" onClick={() => rename(p)} testid="card-rename" />
              <Btn icon="copy" title="Создать копию" onClick={() => act(() => api.duplicateProject(p.id), 'Копия создана')} testid="card-copy" />
              <Btn icon="zip" title="Скачать архив проекта (.zip)" onClick={() => zip(p)} testid="card-zip" />
              <Btn icon="trash" title="В корзину" shortcut="Delete" onClick={() => toTrash(p)} testid="card-trash" />
            </div>
          </div>
        ))}
      </div>

      {trash.length > 0 && (
        <section className="trash" data-testid="trash">
          <button type="button" className="trash-toggle" onClick={() => setShowTrash(!showTrash)} data-testid="trash-toggle" aria-expanded={showTrash}>
            <Icon name={showTrash ? 'down' : 'chevron-right'} size={14} /><Icon name="trash" size={15} /> Корзина ({trash.length})
          </button>
          {showTrash && (
            <div className="trash-list">
              {trash.map((t) => t.byAdmin ? (
                <div key={t.id} className="trash-row trash-row-admin" data-testid="trash-row-admin">
                  <Icon name="shield" size={15} />
                  <span className="trash-name">{t.name}</span>
                  <span className="muted trash-title">{t.title}</span>
                  <span className="muted">удалён администратором{t.trashedBy ? ` (${t.trashedBy})` : ''} {fmtAgo(t.trashed)}</span>
                  <span className="muted small">восстановить может только администратор</span>
                </div>
              ) : (
                <div key={t.id} className="trash-row" data-testid="trash-row">
                  <Icon name={TYPE_ICON[t.type] || 'file'} size={15} />
                  <span className="trash-name">{t.name}</span>
                  <span className="muted trash-title">{t.title}</span>
                  <span className="muted">удалён {fmtAgo(t.trashed)}</span>
                  <Btn icon="restore" label="Восстановить" onClick={() => act(() => shellApi.restoreTrash(t.id), 'Отчёт восстановлен')} testid="trash-restore" />
                  <Btn icon="close" title="Удалить навсегда" danger onClick={async () => {
                    const ok = await askConfirm({ title: 'Удалить навсегда?', text: `«${t.name}» будет удалён с диска без возможности восстановления.`, ok: 'Удалить навсегда', danger: true });
                    if (ok) void act(() => shellApi.purgeTrash([t.id]), 'Удалено');
                  }} testid="trash-purge" />
                </div>
              ))}
              {trash.some((x) => !x.byAdmin) && (
                <div className="trash-foot">
                  <Btn icon="trash" label="Очистить корзину" danger onClick={async () => {
                    const mine = trash.filter((x) => !x.byAdmin);
                    const ok = await askConfirm({ title: 'Очистить корзину?', text: `Отчётов в корзине: ${mine.length}. Они будут удалены с диска навсегда.`, ok: 'Очистить', danger: true });
                    if (ok) void act(() => shellApi.purgeTrash(mine.map((x) => x.id)), 'Корзина очищена');
                  }} />
                </div>
              )}
            </div>
          )}
        </section>
      )}

      <div className="start-foot muted">
        {canReveal && dataDir ? <>
          <Icon name="open" size={13} /> Папка отчётов: <span className="mono">{dataDir}</span>
          <button type="button" className="link-btn" onClick={() => shellApi.revealRoot()}>открыть</button>
        </> : <QuotaNote />}
        <span className="topbar-spacer" />
        <span>Ctrl+K - все команды · F1 - памятка</span>
      </div>
      {sections.map((s) => <s.component key={s.id} ctx={ctx} />)}
      {drag && <div className="drop-hint"><Icon name="file-up" size={40} /><div>Отпустите файл - он откроется как новый отчёт</div></div>}
    </div>
  );
}

/** Окно «Новый отчёт» (id 'project.create'). Результат close(id созданного отчёта). */
export function CreateProjectDialog({ close }: DialogProps<string>) {
  const defaults = useApp((s) => s.defaults);
  const types: { id: string; name: string }[] = defaults?.workTypes || Object.entries(TYPE_NAMES).map(([id, name]) => ({ id, name }));
  const [type, setType] = useState(() => readPref('last.type', 'lab') || 'lab');
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [discipline, setDiscipline] = useState(() => readPref('last.discipline', '') || '');
  const [number, setNumber] = useState('');
  const [template, setTemplate] = useState('standard');
  const [busy, setBusy] = useState(false);
  const auto = [TYPE_NAMES[type], number && `№ ${number}`, discipline].filter(Boolean).join(' ');
  const submit = async () => {
    setBusy(true);
    try {
      const nm = name.trim() || auto || 'Новый отчёт';
      const r = await api.createProject({ name: nm, type, template, meta: { title, discipline, number } });
      writePref('last.type', type);
      writePref('last.discipline', discipline);
      close(r.id);
    } catch (e: any) { ctx.toast(e.message, 'error', 8000); setBusy(false); }
  };
  return (
    <Modal title="Новый отчёт" onClose={() => close()} testid="create-dialog" width={640} footer={<>
      <span className="muted create-hint">Данные студента - из настроек; титул можно дополнить потом («СТО → Титул и данные»).</span>
      <Btn label="Отмена" onClick={() => close()} />
      <Btn label="Создать" primary onClick={submit} disabled={busy} testid="create-submit" />
    </>}>
      <div className="form-grid" onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); void submit(); } }}>
        <Field label="Вид работы" wide>
          <div className="type-grid">
            {types.map((t) => (
              <button key={t.id} type="button" className={'type-card' + (type === t.id ? ' is-active' : '')} onClick={() => setType(t.id)}
                data-testid={'type-' + t.id} aria-pressed={type === t.id}>
                <Icon name={TYPE_ICON[t.id] || 'file'} size={18} /><span>{t.name}</span>
              </button>
            ))}
          </div>
        </Field>
        <Field label="Дисциплина"><input value={discipline} onChange={(e) => setDiscipline(e.target.value)} data-testid="create-discipline" /></Field>
        <Field label="Номер работы"><input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="напр. 3" data-testid="create-number" /></Field>
        <Field label="Тема работы" wide><input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="create-title" /></Field>
        <Field label="Имя файла отчёта" wide hint={`Имя в списке ваших отчётов. Пусто - «${auto || 'Новый отчёт'}».`}>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-name" placeholder={auto} />
        </Field>
        <Field label="Заготовка" wide>
          <Segmented value={template} onChange={setTemplate} testid="create-template"
            options={[{ id: 'standard', label: 'Структура по СТО для вида работы', icon: 'template' }, { id: 'blank', label: 'Пустой документ', icon: 'file' }]} />
        </Field>
      </div>
    </Modal>
  );
}
